import React from 'react';
import styled from 'styled-components';
import InputGroup from '../inputGroup/InputGroup';

// ############################ End Styles ###########################
// tslint:disable:variable-name

const SInputGroupWrapInner = styled.div<{ error: boolean; disabled: boolean }>(({ theme, error, disabled }) => {
  const borderColor = error ? theme.colors.error : theme.colors.border;
  const disabledStyle = disabled ? { opacity: 0.5 } : {};

  return {
    width: 'calc(100% - 2px)',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    border: `1px solid ${borderColor}`,
    borderRadius: theme.borderRadiusCommon,
    position: 'relative',
    '&:focus-within': {
      borderColor: theme.colors.primaryLightBlue,
    },
    ...disabledStyle,
  };
});

const SInputWrap = styled.div(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  '&:not(:first-of-type)': {
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      height: 'calc(100% - 20px)',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 1,
      backgroundColor: theme.colors.border,
    },
  },
}));

const SInput = styled.input(({ theme }) => ({
  textAlign: 'center',
  fontWeight: theme.font.weight.light,
  fontSize: theme.font.size.smallMedium2,
  boxSizing: 'border-box',
  color: theme.colors.primary,
  backgroundColor: 'transparent',
  borderWidth: 0,
  appearance: 'none',
  outline: 'none',
  padding: `${theme.spacing.medium}px 0px`,
  width: '100%',
  maxWidth: '100%',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  '&:invalid': {
    boxShadow: 'none',
  },
  '&::placeholder': {
    fontWeight: theme.font.weight.light,
    color: theme.colors.placeholder,
    fontSize: theme.font.size.smallMedium2,
  },
}));

// tslint:enable:variable-name
// ############################ End Styles ###########################

type CreditCardState = {
  number1: string;
  number2: string;
  number3: string;
  number4: string;
};

export interface ICreditCardProps {
  onFilled?: (creditCard: string, name: string) => void;
  value: string;
  className?: string;
}

interface ICreditCardNumberPartProps {
  name: string;
  dataAt: string;
  dataTestId?: string;
  value: string;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// tslint:disable-next-line:variable-name
const CreditCardNumberPart: React.FC<ICreditCardNumberPartProps> = (props) => {
  const { dataAt, dataTestId, ...inputProps } = props;

  return (
    <SInputWrap>
      <SInput maxLength={4} type="text" inputMode="numeric" pattern="[0-9]*" {...inputProps} data-at={dataAt} data-testid={dataTestId} />
    </SInputWrap>
  );
};

export class CreditCard extends React.Component<ICreditCardProps, CreditCardState> {
  public static defaultProps = {
    className: '',
  };

  public state = {
    number1: '',
    number2: '',
    number3: '',
    number4: '',
  };

  public handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ ...this.state, [event.currentTarget.name]: '' });
    if (this.props.onFilled) {
      this.props.onFilled('', 'cardNumber');
    }
  };

  public handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const el = event.currentTarget;
    const aNumber = /^\d+$/.test(el.value);
    if (!aNumber) {
      const newValue = el.value.length <= 1 ? '' : el.value.slice(0, -1);
      this.setState({ ...this.state, [el.name]: newValue });
    }
    const val = this.state.number1 + this.state.number2 + this.state.number3 + this.state.number4;
    if (val.length === 16 && /^\d+$/.test(val)) {
      if (this.props.onFilled) {
        this.props.onFilled(val, 'cardNumber');
      }
    } else if (
      el.value.length === 4 &&
      aNumber &&
      el.parentElement &&
      el.parentElement.nextElementSibling &&
      el.parentElement.nextElementSibling.firstChild &&
      (el.parentElement.nextElementSibling.firstChild as HTMLInputElement).value.length < 4
    ) {
      (el.parentElement.nextElementSibling.firstChild as HTMLElement).focus();
    }
  };

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valueCandidate = event.currentTarget.value;
    if (valueCandidate.length <= 4) {
      this.setState({ ...this.state, [event.currentTarget.name]: valueCandidate });
    }
  };

  public static getDerivedStateFromProps(nextProps: ICreditCardProps, prevState: CreditCardState) {
    if (prevState.number1 === '' && prevState.number2 === '' && prevState.number3 === '' && prevState.number4 === '' && nextProps.value.length === 16) {
      return {
        number1: nextProps.value.substring(0, 4),
        number2: nextProps.value.substring(4, 8),
        number3: nextProps.value.substring(8, 12),
        number4: nextProps.value.substring(12, 16),
      };
    }
    return {};
  }

  public render() {
    const commonInputProps = {
      onFocus: this.handleFocus,
      onKeyUp: this.handleKeyUp,
      onChange: this.handleChange,
    };

    return (
      <InputGroup labelColor={'primary'} className={this.props.className} dataAt="creditCardNumber" label="Card number">
        <SInputGroupWrapInner error={false} disabled={false} data-testid="credit-card-number">
          <CreditCardNumberPart {...commonInputProps} name="number1" value={this.state.number1} dataAt="creditCardNum1" dataTestId="card-number-input-1" />
          <CreditCardNumberPart {...commonInputProps} name="number2" value={this.state.number2} dataAt="creditCardNum2" dataTestId="card-number-input-2" />
          <CreditCardNumberPart {...commonInputProps} name="number3" value={this.state.number3} dataAt="creditCardNum3" dataTestId="card-number-input-3" />
          <CreditCardNumberPart {...commonInputProps} name="number4" value={this.state.number4} dataAt="creditCardNum4" dataTestId="card-number-input-4" />
        </SInputGroupWrapInner>
      </InputGroup>
    );
  }
}
